/**
 * Shared engagement/role-list types + pillar tokens (SPEC_ROLE_ORGANIZED_INBOX
 * §4a). A SelectableEntry is "a role this user holds" — the unit the Action
 * Center rail, the My Impact rail's Roles/Projects lenses, and the role
 * reference page all read.
 *
 * RELOCATED here in DEV-1476 (2026-07-13) from Components/EngagementList/.
 * That folder was the RETIRED EngagementsPage's (Track-4 Slice F): its UI —
 * EngagementCard.tsx + engagementCards.module.css — was dead code with no
 * importers, so the folder is now DELETED. Only the data survived, and data
 * belongs under src/Data/, not under a Components folder named after a page
 * that no longer exists. Nothing renders a SelectableEntry as a "card" any
 * more; four live surfaces consume it as a type.
 */
import type { PillarType } from "../../lib/pillarTypes";

export type SelectableTypeTag =
  | "full-project"
  | "mini-project"
  | "workspace"
  | "organization"
  | "community";

/**
 * Discriminated source back-reference. `data` is loosely typed at the shared
 * boundary so the heavy EnrichedRole / CommunityEngagement / WorkspaceEngagement
 * interfaces stay in their origin; consumers narrow on `type` and cast as needed.
 */
export type SelectableSource =
  | { type: "role"; data: unknown }
  | { type: "community"; data: unknown }
  | { type: "workspace"; data: unknown };

export interface SelectableEntry {
  /** `role:<assignmentGuid>` | `comm:<entityGuid>:<role>` | `ws:<workspaceGuid>` */
  key: string;
  pillar: PillarType;
  /** Top line — the role title (e.g. "Secretary", "Lead"). */
  roleLabel: string;
  /** Second line — entity name / location breadcrumb. */
  entityName: string;
  typeTag: SelectableTypeTag;
  /** Meta line — community name / project name / phase. */
  secondary: string;
  /** Granted within the last 3 days → shows the "new" pill. */
  isNew: boolean;
  /** Square thumbnail (projects/workspaces). */
  heroUrl: string | null;
  heroSource: "own" | "parent-community" | "none";
  /** Circle avatar (orgs/communities), ancestor-cascaded. */
  logoUrl: string | null;
  owningCommunityName: string | null;
  owningCommunityGuid: string | null;
  /**
   * Canonical community this entry groups under in the Action Center rail
   * (SPEC_ROLE_ORGANIZED_INBOX §4a): the entity itself for community/org roles,
   * the owning community for project/role entries, the workspace's community for
   * workspaces. Null when no community resolves (rare). The rail groups by this.
   */
  groupCommunityGuid: string | null;
  groupCommunityName: string | null;
  /**
   * DEV-1368. A PREVIOUS role — one the user no longer actively holds. Hidden
   * from the rail by default; revealed by the "Show previous roles" toggle,
   * rendered muted.
   */
  isPrevious?: boolean;
  /**
   * Why the role is previous (distinguishes the two cases Skip 2026-06-24):
   *  - "project_ended" — the project itself closed out (Completed/Abandoned);
   *  - "role_ended" — the user left the role (completed/resigned) but the
   *    project is still in flight (someone else may hold the role now).
   */
  previousKind?: "project_ended" | "role_ended";
  source: SelectableSource;
}

/** Section render order (left list). The `enablers` group renders as "Organizations". */
export const PILLAR_RENDER_ORDER: PillarType[] = [
  "projects",
  "enablers",
  "communities",
  "problems",
  "solutions",
];

/** Pillar key → CSS token (canonical pillar taxonomy, theme-variables.css). */
export const PILLAR_CSS_TOKEN: Record<PillarType, string> = {
  projects: "var(--pillar-projects)",
  communities: "var(--pillar-communities)",
  problems: "var(--pillar-problems)",
  solutions: "var(--pillar-solutions)",
  enablers: "var(--pillar-enablers)",
};

// DEV-1164 pillar-specific row tints (dark-theme-tuned; raw hex is an existing
// token-rule exception carried over from EngagementsPage). BG = card tint;
// STRIPE = the left-accent color.
export const PILLAR_BG_TOKEN: Record<PillarType, string> = {
  projects: "#21262f",
  communities: "#3e4452",
  problems: "#2f1a1e",
  solutions: "#182f22",
  enablers: "#182947",
};

export const PILLAR_STRIPE_TOKEN: Record<PillarType, string> = {
  projects: "#a3acba",
  communities: "#ffffff",
  problems: "#e15f5f",
  solutions: "#6cd28c",
  enablers: "#7fb6f3",
};
